import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon, UserPlusIcon, CalendarDaysIcon, ClockIcon, PhoneIcon, UserIcon, XMarkIcon
} from '@heroicons/react/24/outline';
import { MessageCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import useDebouncedValue from '../hooks/useDebouncedValue';
import PatientSelfBookingSettings from '../pages/PatientSelfBookingSettings';
import useAuthStore from '../store/useAuthStore';

const SCHEDULE_NOW_BUFFER_MINUTES = 5;

export default function AddAppointmentModal({ isOpen, closeModal, onAppointmentCreated, initialData }) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const capabilitySet = new Set(user?.capabilities || []);
  const canOpenClinicalPad = Array.isArray(user?.capabilities)
    ? capabilitySet.has('clinical.edit') && capabilitySet.has('clinical.prescription.open')
    : ['doctor_admin', 'doctor'].includes(String(user?.role || '').toLowerCase());
  const liveSearchRequestRef = useRef(0);
  const submitInFlightRef = useRef(false);
  const [appointmentTab, setAppointmentTab] = useState('clinic');
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctorOptionsLoading, setDoctorOptionsLoading] = useState(false);
  const [doctorOptionsError, setDoctorOptionsError] = useState('');

  // STATE PRESERVED EXACTLY
  const [step, setStep] = useState('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    gender: '',
    profession: '',
    address: '',
    visitType: 'New Consultation',
    assignedDoctor: '',
    assignedDoctorId: '',
    duration: '30',
    chair: '',
    previousCaseReference: '',
  });
  const [schedulingMode, setSchedulingMode] = useState(null);
  const [schedulingError, setSchedulingError] = useState('');
  const [appointmentDraft, setAppointmentDraft] = useState({
    date: '',
    time: '',
    assignedDoctor: '',
    assignedDoctorId: '',
    duration: '30',
    chair: '',
  });
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [savedAppointmentResult, setSavedAppointmentResult] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  useEffect(() => {
    if (initialData?.visitType) {
      setFormData((prev) => ({ ...prev, visitType: initialData.visitType }));
    }
  }, [initialData]);

  useEffect(() => {
    if (!isOpen) return;
    const currentSlot = getCurrentLocalAppointmentSlot(10);
    setAppointmentDraft({
      date: initialData?.date || currentSlot.date,
      time: initialData?.time || currentSlot.time.slice(0, 5),
      assignedDoctor: initialData?.doctorAssigned || '',
      duration: initialData?.duration || '30',
      chair: initialData?.chairNumber || '',
    });
  }, [initialData, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setDoctorOptionsLoading(true);
    setDoctorOptionsError('');
    api.get('/patients/appointment/doctor-options').then(({ data }) => {
      if (!active) return;
      const doctors = Array.isArray(data?.doctors) ? data.doctors : [];
      setDoctorOptions(doctors);
      setAppointmentDraft((draft) => {
        const existing = doctors.find((doctor) => doctor.displayName === draft.assignedDoctor);
        const selected = existing || (doctors.length === 1 ? doctors[0] : null);
        return selected ? { ...draft, assignedDoctorId:String(selected.id), assignedDoctor:selected.displayName } : { ...draft, assignedDoctorId:'' };
      });
    }).catch((error) => {
      if (!active) return;
      setDoctorOptions([]);
      setDoctorOptionsError(error?.response?.data?.error || 'Active doctors could not be loaded.');
    }).finally(() => active && setDoctorOptionsLoading(false));
    return () => { active = false; };
  }, [isOpen]);

  const resetForm = () => {
    setAppointmentTab('clinic');
    setStep('search');
    setSearchTerm('');
    setSearchResults([]);
    setSelectedPatient(null);
    setFormData({
      name: '', age: '', phone: '', gender: '', address: '',
      profession: '',
      visitType: initialData?.visitType || 'New Consultation',
      assignedDoctor: '',
      duration: '30',
      chair: '',
      previousCaseReference: '',
    });
    const currentSlot = getCurrentLocalAppointmentSlot(10);
    setSchedulingMode(null);
    setSchedulingError('');
    setAppointmentDraft({
      date: initialData?.date || currentSlot.date,
      time: initialData?.time || currentSlot.time.slice(0, 5),
      assignedDoctor: initialData?.doctorAssigned || '',
      assignedDoctorId: '',
      duration: initialData?.duration || '30',
      chair: initialData?.chairNumber || '',
    });
    setSearching(false);
    setSearchError('');
    setSubmitting(false);
    setSubmitMode('');
    setSendingWhatsApp(false);
    setSavedAppointmentResult(null);
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const validateWhatsApp = (phone) => /^[6-9]\d{9}$/.test(String(phone || '').trim());
  const normalizePhone = (phone) => String(phone || '').replace(/\D/g, '').slice(-10);
  const normalizeVisitTypeForApi = (visitType) =>
    visitType === 'Follow-up' ? 'Follow-up' : 'Consultation';
  const buildAppointmentWhatsAppStatusMessage = (status) => {
    if (!status) {
      return 'Appointment saved. WhatsApp confirmation status unavailable.';
    }

    if (status.delivered) {
      return 'Appointment saved. WhatsApp confirmation sent.';
    }

    if (status.deliveryState === 'not_configured') {
      return 'Appointment saved. WhatsApp confirmation not configured.';
    }

    if (status.deliveryState === 'failed') {
      return 'Appointment saved, but WhatsApp confirmation could not be queued.';
    }

    if (status.deliveryState === 'no_patient_phone') {
      return 'Appointment saved. WhatsApp confirmation queued, but patient WhatsApp number is missing.';
    }

    return 'Appointment saved. WhatsApp confirmation queued.';
  };

  const normalizePatientSearchResults = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.patients)) {
      return payload.patients;
    }

    if (Array.isArray(payload?.data?.patients)) {
      return payload.data.patients;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    return [];
  };

  const closeSchedulingModal = () => {
    if (submitting) return;
    setSchedulingMode(null);
  };

  const getCurrentLocalAppointmentSlot = (offsetMinutes = 0) => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + offsetMinutes);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}:${seconds}`,
    };
  };

  const parseTimeToMinutes = (value) => {
    const [hours, minutes] = String(value || '').split(':').map((part) => Number(part));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
  };

  const isAppointmentDraftInPast = (draft) => {
    if (!draft?.date || !draft?.time) return false;

    const currentSlot = getCurrentLocalAppointmentSlot();
    const draftMinutes = parseTimeToMinutes(draft.time);
    const currentMinutes = parseTimeToMinutes(currentSlot.time);

    if (draftMinutes === null || currentMinutes === null) return false;
    if (draft.date < currentSlot.date) return true;
    return draft.date === currentSlot.date && draftMinutes < currentMinutes;
  };

  const normalizeScheduleNowDraft = (draft) => {
    if (!draft?.date || !draft?.time) return draft;

    const safeSlot = getCurrentLocalAppointmentSlot(SCHEDULE_NOW_BUFFER_MINUTES);
    const draftMinutes = parseTimeToMinutes(draft.time);
    const safeMinutes = parseTimeToMinutes(safeSlot.time);

    if (
      draftMinutes !== null &&
      safeMinutes !== null &&
      draft.date === safeSlot.date &&
      draftMinutes < safeMinutes
    ) {
      return {
        ...draft,
        time: safeSlot.time.slice(0, 5),
      };
    }

    return draft;
  };

  const buildClinicalPadRoute = ({ patientId, appointmentId }) => {
    if (!patientId || !appointmentId) {
      return '';
    }

    const params = new URLSearchParams({
      patientId: String(patientId),
      appointmentId: String(appointmentId),
      mode: 'pad',
    });

    return `/prescriptions/new?${params.toString()}`;
  };

  const buildPatientRecordRoute = (patientId) => {
    if (!patientId) {
      return '';
    }

    const params = new URLSearchParams({
      patientId: String(patientId),
    });

    return `/patients?${params.toString()}`;
  };

  const openSchedulingModal = (mode) => {
    const currentSlot = getCurrentLocalAppointmentSlot(
      mode === 'now' ? SCHEDULE_NOW_BUFFER_MINUTES : 0
    );
    setSchedulingMode(mode);
    setSchedulingError('');
    const configuredDoctor = doctorOptions.find((doctor) => doctor.displayName === (formData.assignedDoctor || initialData?.doctorAssigned))
      || (doctorOptions.length === 1 ? doctorOptions[0] : null);
    setAppointmentDraft({
      date: mode === 'now' ? currentSlot.date : '',
      time: mode === 'now' ? currentSlot.time.slice(0, 5) : '',
      assignedDoctor: configuredDoctor?.displayName || '',
      assignedDoctorId: configuredDoctor ? String(configuredDoctor.id) : '',
      duration: formData.duration || initialData?.duration || '30',
      chair: formData.chair || initialData?.chairNumber || '',
    });
  };

  const updateAppointmentDraft = (field, value) => {
    setSchedulingError('');
    setSavedAppointmentResult(null);
    setAppointmentDraft((current) => ({ ...current, [field]: value }));
  };

  const validateAppointmentDraft = (draft) => {
    if (!draft?.date || !draft?.time) {
      const message = 'Please select appointment date and time';
      setSchedulingError(message);
      toast.error(message);
      return false;
    }

    if (!String(draft.assignedDoctorId || '').trim()) {
      const message = 'Assigned Doctor is required';
      setSchedulingError(message);
      toast.error(message);
      return false;
    }

    const duration = Number(draft.duration);
    if (!Number.isFinite(duration) || duration < 5) {
      const message = 'Duration must be at least 5 minutes';
      setSchedulingError(message);
      toast.error(message);
      return false;
    }

    if (!String(draft.chair || '').trim()) {
      const message = 'Chair is required';
      setSchedulingError(message);
      toast.error(message);
      return false;
    }

    if (isAppointmentDraftInPast(draft)) {
      const message = 'Please choose a current or future appointment time.';
      setSchedulingError(message);
      toast.error(message);
      return false;
    }

    setSchedulingError('');
    return true;
  };

  const normalizeAppointmentSaveResponse = (payload, fallbackPatientId) => {
    const appointment =
      payload?.appointment ||
      payload?.data?.appointment ||
      payload?.data?.row ||
      payload?.row ||
      payload ||
      {};
    const patient =
      appointment?.Patient ||
      appointment?.patient ||
      payload?.Patient ||
      payload?.patient ||
      payload?.data?.Patient ||
      payload?.data?.patient ||
      {};

    const patientId =
      appointment?.patientId ||
      appointment?.patient_id ||
      payload?.patientId ||
      payload?.patient_id ||
      payload?.data?.patientId ||
      payload?.data?.patient_id ||
      patient?.id ||
      fallbackPatientId ||
      null;
    const appointmentId =
      appointment?.appointmentId ||
      appointment?.id ||
      appointment?.appointment_id ||
      payload?.appointmentId ||
      payload?.appointment_id ||
      payload?.id ||
      payload?.data?.appointmentId ||
      payload?.data?.appointment_id ||
      payload?.data?.id ||
      null;

    return {
      appointment,
      patientId,
      appointmentId,
    };
  };

  const normalizePatientCreateResponse = (payload) => {
    const patient =
      payload?.patient ||
      payload?.data?.patient ||
      payload?.row ||
      payload?.data ||
      payload ||
      {};

    return {
      patient,
      patientId:
        patient?.id ||
        payload?.patientId ||
        payload?.patient_id ||
        payload?.existingPatientId ||
        payload?.existing_patient_id ||
        payload?.data?.patientId ||
        payload?.data?.patient_id ||
        null,
      existing: Boolean(payload?.existing || payload?.data?.existing),
      message: payload?.message || payload?.data?.message || '',
    };
  };

  const runPatientSearch = async (term, options = {}) => {
    const { showErrorToast = false } = options;
    const trimmedTerm = String(term || '').trim();

    if (trimmedTerm.length < 2) {
      liveSearchRequestRef.current += 1;
      setSearchResults([]);
      setSearching(false);
      setSearchError('');
      return [];
    }

    const requestId = liveSearchRequestRef.current + 1;
    liveSearchRequestRef.current = requestId;
    setSearching(true);
    setSearchError('');

    try {
      const res = await api.get('/patients/search', {
        params: { q: trimmedTerm },
      });

      const nextResults = normalizePatientSearchResults(res.data);
      if (liveSearchRequestRef.current === requestId) {
        setSearchResults(nextResults);
        setSearchError('');
      }
      return nextResults;
    } catch (err) {
      console.error(err);
      if (liveSearchRequestRef.current === requestId) {
        setSearchResults([]);
        setSearchError(err.response?.data?.error || 'Patient search is unavailable. Please try again.');
      }
      if (showErrorToast) {
        toast.error(err.response?.data?.error || 'Search failed');
      }
      return [];
    } finally {
      if (liveSearchRequestRef.current === requestId) {
        setSearching(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen || step !== 'search') return;

    const trimmedTerm = debouncedSearchTerm.trim();
    if (trimmedTerm.length < 2) {
      setSearchResults([]);
      setSearching(false);
      setSearchError('');
      return;
    }

    runPatientSearch(trimmedTerm);
  }, [debouncedSearchTerm, isOpen, step]);

  const handleSearch = async () => {
    if (String(searchTerm || '').trim().length < 2) {
      toast.error('Type at least 2 characters to search patients.');
      return;
    }

    const results = await runPatientSearch(searchTerm, { showErrorToast: true });
    if (String(searchTerm || '').trim() && results.length === 0) {
      toast.error('No records found');
    }
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    setStep('register');
    setSearchTerm(patient.name || '');
    setSearchResults([]);
    setSearchError('');
    setFormData({
      name: patient.name || '',
      age: patient.age || '',
      phone: patient.phone || '',
      gender: patient.gender || '',
      profession: patient.profession || '',
      address: patient.address || '',
      visitType: initialData?.visitType || 'New Consultation',
      assignedDoctor: initialData?.doctorAssigned || '',
      duration: initialData?.duration || '30',
      chair: initialData?.chairNumber || '',
      previousCaseReference: '',
    });
  };

  const buildAppointmentSaveSignature = (appointmentDetailsForSave) => JSON.stringify({
    selectedPatientId: selectedPatient?.id || null,
    name: String(formData.name || '').trim(),
    phone: normalizePhone(formData.phone),
    visitType: normalizeVisitTypeForApi(formData.visitType),
    previousCaseReference:
      normalizeVisitTypeForApi(formData.visitType) === 'Follow-up'
        ? formData.previousCaseReference || null
        : null,
    date: appointmentDetailsForSave?.date || '',
    time: appointmentDetailsForSave?.time || '',
    doctorAssigned: String(appointmentDetailsForSave?.assignedDoctor || '').trim(),
    chairNumber: String(appointmentDetailsForSave?.chair || '').trim(),
    duration: String(appointmentDetailsForSave?.duration || '').trim(),
  });

  const getAppointmentSaveErrorMessage = (err) => {
    let message = 'Appointment could not be saved. Please try again.';
    if (err.response?.status === 409) {
      message = 'Duplicate appointment exists for this patient at the selected date/time.';
    } else if (err.response?.status === 429) {
      message = 'Too many attempts. Please wait a moment and try again.';
    } else if (err.response?.status === 400) {
      message = err.response?.data?.error || 'Please check the appointment details.';
    } else if (err.response?.status === 401 || err.response?.status === 403) {
      message = err.response?.data?.error || 'You do not have permission to schedule this appointment.';
    } else {
      message = err.response?.data?.error || err.message || message;
    }
    return message;
  };

  const saveAppointmentOnce = async ({ openPadAfterSave = false, appointmentDetails = null } = {}) => {
    if (!formData.name || !formData.phone) {
      throw new Error('Name and phone are required');
    }
    if (!validateWhatsApp(formData.phone)) {
      throw new Error('Invalid WhatsApp number. Must be 10 digits starting with 6/7/8/9.');
    }

    const appointmentDetailsForSave = openPadAfterSave
      ? normalizeScheduleNowDraft(appointmentDetails)
      : appointmentDetails;

    if (appointmentDetailsForSave !== appointmentDetails) {
      setAppointmentDraft(appointmentDetailsForSave);
    }

    if (!validateAppointmentDraft(appointmentDetailsForSave)) {
      return null;
    }

    const saveSignature = buildAppointmentSaveSignature(appointmentDetailsForSave);
    if (savedAppointmentResult?.signature === saveSignature && savedAppointmentResult?.appointment?.id) {
      return savedAppointmentResult;
    }

    const normalizedPhone = normalizePhone(formData.phone);
    const visitTypeForApi = normalizeVisitTypeForApi(formData.visitType);
    const selectedDate = appointmentDetailsForSave.date;
    const selectedTime = appointmentDetailsForSave.time;
    let patientId = selectedPatient?.id;

    if (!selectedDate || !selectedTime) {
      throw new Error('Please select appointment date and time');
    }

    if (!patientId) {
      let patientPayload = null;

      try {
        const res = await api.post('/patients', {
          name: formData.name,
          phone: normalizedPhone,
          age: formData.age,
          gender: formData.gender,
          profession: formData.profession,
          address: formData.address,
        });
        patientPayload = res.data;
      } catch (patientErr) {
        const conflictPatient = patientErr.response?.data?.patient || patientErr.response?.data?.existingPatient;
        const conflictPatientId =
          conflictPatient?.id ||
          patientErr.response?.data?.patientId ||
          patientErr.response?.data?.existingPatientId ||
          null;

        if (patientErr.response?.status === 409 && conflictPatientId) {
          patientPayload = {
            patient: conflictPatient || { id: conflictPatientId },
            existing: true,
            message: patientErr.response?.data?.message || 'Existing patient linked by phone number.',
          };
        } else {
          throw patientErr;
        }
      }

      const normalizedPatient = normalizePatientCreateResponse(patientPayload);
      patientId = normalizedPatient.patientId || patientId;

      if (normalizedPatient.existing && normalizedPatient.message) {
        toast.success(normalizedPatient.message);
      }
    }

    if (!patientId) {
      throw new Error('Patient record could not be created or linked.');
    }

    const appointmentRes = await api.post('/patients/appointment/schedule-later', {
      patientId,
      date: selectedDate,
      time: selectedTime,
      visitType: visitTypeForApi,
      doctorAssigned: appointmentDetailsForSave.assignedDoctor.trim(),
      assignedDoctorId: appointmentDetailsForSave.assignedDoctorId,
      chairNumber: appointmentDetailsForSave.chair,
      duration: appointmentDetailsForSave.duration,
      previousCaseReference:
        visitTypeForApi === 'Follow-up' ? formData.previousCaseReference || null : null,
    });

    const normalizedSaveResult = normalizeAppointmentSaveResponse(appointmentRes.data, patientId);
    const whatsappStatus =
      appointmentRes.data?.whatsappStatus ||
      normalizedSaveResult.appointment?.whatsappStatus ||
      null;
    const nextAppointment = {
      ...(normalizedSaveResult.appointment || {}),
      id: normalizedSaveResult.appointmentId,
      patientId: normalizedSaveResult.patientId,
      patientName: formData.name,
      phone: normalizedPhone,
      visitType: visitTypeForApi,
      doctorAssigned: appointmentDetailsForSave.assignedDoctor.trim(),
      chairNumber: appointmentDetailsForSave.chair,
      duration: appointmentDetailsForSave.duration,
    };
    const clinicalPadRoute = buildClinicalPadRoute({
      patientId: nextAppointment.patientId,
      appointmentId: nextAppointment.id,
    });
    const patientRecordRoute = buildPatientRecordRoute(nextAppointment.patientId);

    if (!nextAppointment.patientId) {
      throw new Error('Appointment was saved but patient ID was missing.');
    }
    if (!nextAppointment.id) {
      throw new Error('Appointment was saved but appointment ID was missing.');
    }

    const result = {
      signature: saveSignature,
      appointment: nextAppointment,
      clinicalPadRoute,
      patientRecordRoute,
      whatsappStatus,
    };
    setSavedAppointmentResult(result);

    const whatsappToastMessage = buildAppointmentWhatsAppStatusMessage(whatsappStatus);
    if (whatsappStatus?.delivered) {
      toast.success(whatsappToastMessage);
    } else {
      toast(whatsappToastMessage);
    }

    return result;
  };

  const handleSubmit = async ({ openPadAfterSave = false, appointmentDetails = null } = {}) => {
    if (submitting || submitInFlightRef.current) {
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);
    setSubmitMode(openPadAfterSave ? 'openPad' : 'save');
    try {
      const saveResult = await saveAppointmentOnce({ openPadAfterSave, appointmentDetails });
      if (!saveResult) return;

      const {
        appointment: nextAppointment,
        clinicalPadRoute,
        patientRecordRoute,
        whatsappStatus,
      } = saveResult;

      try {
        if (onAppointmentCreated) {
          await onAppointmentCreated({
            openPad: openPadAfterSave,
            clinicalPadRoute,
            patientRecordRoute,
            appointment: nextAppointment,
            whatsappStatus,
          });
          resetForm();
          closeModal();
          return;
        }

        if (!openPadAfterSave) {
          resetForm();
          closeModal();
          return;
        }

        if (clinicalPadRoute) {
          toast.success('Appointment saved. Opening Prescription Pad.');
          navigate(clinicalPadRoute);
          resetForm();
          closeModal();
          return;
        }

        const message = 'Appointment was saved but Prescription Pad could not open because appointment ID was missing.';
        setSchedulingError(message);
        toast.error(message);
      } catch (navigationErr) {
        console.error(navigationErr);

        const message = 'Appointment saved, but the next page could not be opened.';
        setSchedulingError(message);
        toast.error(message);
      }
    } catch (err) {
      console.error(err);
      const message = getAppointmentSaveErrorMessage(err);
      setSchedulingError(message);
      toast.error(message);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
      setSubmitMode('');
    }
  };

  const handleSendWhatsAppConfirmation = async () => {
    if (submitting || sendingWhatsApp || submitInFlightRef.current) {
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);
    setSendingWhatsApp(true);
    setSubmitMode('whatsapp');

    try {
      const saveResult = await saveAppointmentOnce({
        openPadAfterSave: schedulingMode === 'now' && canOpenClinicalPad,
        appointmentDetails: appointmentDraft,
      });
      if (!saveResult?.appointment?.id) {
        throw new Error('Appointment was saved but appointment ID was missing.');
      }

      const res = await api.post(`/patients/appointment/${saveResult.appointment.id}/remind`);
      const whatsappUrl = res.data?.whatsappUrl;
      if (whatsappUrl) {
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      }

      toast.success('WhatsApp confirmation ready to send');
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || getAppointmentSaveErrorMessage(err);
      setSchedulingError(message);
      toast.error(message);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
      setSendingWhatsApp(false);
      setSubmitMode('');
    }
  };

  const showNoPatientRecordFound =
    step === 'search' &&
    !searching &&
    !searchError &&
    debouncedSearchTerm.trim().length >= 2 &&
    debouncedSearchTerm.trim() === searchTerm.trim() &&
    searchResults.length === 0;
  const selectedPatientCode =
    selectedPatient?.patientId || selectedPatient?.customerId || selectedPatient?.patientCode || '';
  const selectedPatientFieldClasses = selectedPatient
    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
    : 'bg-slate-50 border-slate-200 text-slate-900';

  const schedulingDialog = schedulingMode ? (
    <div className="fixed inset-0 z-[80] flex min-h-screen items-start justify-center overflow-y-auto bg-slate-900/50 p-2 pt-4 backdrop-blur-sm sm:items-center sm:p-4">
      <Dialog.Panel className="max-h-[calc(100dvh-1rem)] w-full max-w-2xl overflow-y-auto rounded-[24px] border border-slate-200 bg-white shadow-2xl sm:rounded-[28px]">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-4 py-4 sm:px-6 sm:py-5">
          <div>
            <p className="text-[11px] font-black uppercase tracking-widest text-blue-600">Appointment Scheduling</p>
            <h4 className="mt-1 text-xl font-black text-slate-900">
              {schedulingMode === 'now' ? 'Schedule Now' : 'Schedule Later'}
            </h4>
          </div>
          <button
            type="button"
            onClick={closeSchedulingModal}
            disabled={submitting}
            className="rounded-full bg-slate-50 p-2 text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-4 sm:p-6 md:grid-cols-2">
          <div>
            <label className="mb-2 ml-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Date</label>
            <div className="relative">
              <CalendarDaysIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={appointmentDraft.date}
                onChange={(e) => updateAppointmentDraft('date', e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-12 pr-4 font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 ml-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Time</label>
            <div className="relative">
              <ClockIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="time"
                value={appointmentDraft.time}
                onChange={(e) => updateAppointmentDraft('time', e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-12 pr-4 font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 ml-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Assigned Doctor *</label>
            <select
              value={appointmentDraft.assignedDoctorId || ''}
              onChange={(event) => {
                const doctor = doctorOptions.find((item) => String(item.id) === event.target.value);
                setAppointmentDraft((draft) => ({ ...draft, assignedDoctorId:event.target.value, assignedDoctor:doctor?.displayName || '' }));
              }}
              disabled={doctorOptionsLoading || doctorOptions.length === 0}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
            ><option value="">{doctorOptionsLoading ? 'Loading doctors…' : 'Select doctor'}</option>{doctorOptions.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.label || doctor.displayName}</option>)}</select>
            {!doctorOptionsLoading && doctorOptions.length === 0 ? <p className="mt-2 text-sm font-semibold text-amber-700">{doctorOptionsError || 'No active doctor is configured for this clinic.'}</p> : null}
          </div>

          <div>
            <label className="mb-2 ml-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Duration</label>
            <input
              type="number"
              min="5"
              step="5"
              value={appointmentDraft.duration}
              onChange={(e) => updateAppointmentDraft('duration', e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="30"
            />
          </div>

          <div>
            <label className="mb-2 ml-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Chair</label>
            <input
              type="number"
              min="1"
              value={appointmentDraft.chair}
              onChange={(e) => updateAppointmentDraft('chair', e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Chair number"
            />
          </div>
        </div>

        <div className="mobile-sticky-actions border-t border-slate-100 bg-white px-4 py-4 sm:px-6 sm:py-5">
          {schedulingError ? (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {schedulingError}
            </div>
          ) : null}
          <div className="flex flex-col items-stretch justify-end gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={closeSchedulingModal}
              disabled={submitting || sendingWhatsApp}
              className="tap-target rounded-full px-5 py-2.5 text-sm font-bold text-slate-600 transition-all hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSendWhatsAppConfirmation}
              disabled={submitting || sendingWhatsApp}
              className="tap-target inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-6 py-3 text-sm font-black text-emerald-700 transition-all hover:bg-emerald-100 disabled:opacity-50"
            >
              {sendingWhatsApp || submitMode === 'whatsapp'
                ? 'Preparing WhatsApp...'
                : (
                  <>
                    <MessageCircle className="h-4 w-4" />
                    Send on WhatsApp
                  </>
                )}
            </button>
            <button
              type="button"
              onClick={() =>
                handleSubmit({
                  openPadAfterSave: schedulingMode === 'now' && canOpenClinicalPad,
                  appointmentDetails: appointmentDraft,
                })
              }
              disabled={submitting || sendingWhatsApp}
              className="tap-target rounded-full bg-blue-600 px-7 py-3 text-sm font-black text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting && submitMode === 'openPad'
                ? 'Opening Pad...'
                : submitting && submitMode === 'save'
                  ? 'Saving...'
                  : schedulingMode === 'now' && canOpenClinicalPad
                    ? 'Save and Open Pad'
                    : 'Save Appointment'}
            </button>
          </div>
        </div>
      </Dialog.Panel>
    </div>
  ) : null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-2 pt-4 text-center sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-4 scale-95"
            >
              {/* MODAL CONTAINER - MATCHES PICTURE 3 & 4 STYLING */}
              <Dialog.Panel className="max-h-[calc(100dvh-1rem)] w-full max-w-[800px] transform overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-[#F8FAFC] text-left align-middle shadow-2xl transition-all sm:rounded-[2rem]">
                
                {/* HEADER */}
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-4 py-4 sm:px-8 sm:py-6">
                  <div>
                    <Dialog.Title as="h3" className="text-xl font-black text-slate-900 sm:text-2xl">
                      Add Appointment
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-full bg-slate-50 p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="border-b border-slate-200 bg-white px-4 pt-3 sm:px-8">
                  <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1" role="tablist" aria-label="Add appointment options">
                    <button type="button" role="tab" aria-selected={appointmentTab === 'clinic'} onClick={() => setAppointmentTab('clinic')} className={`rounded-xl px-3 py-3 text-sm font-bold ${appointmentTab === 'clinic' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'}`}>Book at Clinic</button>
                    <button type="button" role="tab" aria-selected={appointmentTab === 'self-booking'} onClick={() => setAppointmentTab('self-booking')} className={`rounded-xl px-3 py-3 text-sm font-bold ${appointmentTab === 'self-booking' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600'}`}>Patient Self-Booking</button>
                  </div>
                </div>

                {appointmentTab === 'self-booking' ? (
                  <div className="p-3 sm:p-8"><PatientSelfBookingSettings embedded /></div>
                ) : <>
                <div className="space-y-6 p-3 sm:space-y-8 sm:p-8">
                  {/* SECTION A: SEARCH (Matches Picture 3) */}
                  <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[24px] sm:p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="relative flex-1">
                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search by Patient Name, Phone Number, or Patient ID"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                          className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-2xl pl-12 pr-4 py-4 focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none font-medium transition-all"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSearch}
                        disabled={searching}
                      className="tap-target rounded-2xl bg-slate-900 px-8 py-4 text-sm font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-50"
                      >
                        {searching ? 'Searching...' : 'Search Patient'}
                      </button>
                    </div>

                    {searching && searchTerm.trim().length >= 2 ? (
                      <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 px-5 py-4 text-sm font-semibold text-blue-700">
                        Searching patients...
                      </div>
                    ) : null}

                    {searchError ? (
                      <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
                        {searchError}
                      </div>
                    ) : null}

                    {showNoPatientRecordFound ? (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-800">
                        No records found
                      </div>
                    ) : null}

                    {searchResults.length > 0 && (
                      <div className="mt-4 border border-slate-200 rounded-2xl overflow-hidden bg-white">
                        <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                          {searchResults.map((p) => (
                            <li
                              key={p.id}
                              onClick={() => selectPatient(p)}
                              className="group flex cursor-pointer flex-col gap-3 px-4 py-4 transition-colors hover:bg-blue-50 sm:flex-row sm:items-center sm:justify-between sm:px-6"
                            >
                              <div>
                                <p className="font-bold text-slate-900">{p.name}</p>
                                <p className="text-xs font-medium text-slate-500 mt-1">
                                  {p.phone || 'Phone not available'}
                                  {p.patientId || p.customerId || p.patientCode
                                    ? ` | ID: ${p.patientId || p.customerId || p.patientCode}`
                                    : ''}
                                </p>
                              </div>
                              <span className="text-xs font-bold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">Select</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-slate-500 font-medium">If patient is not found in database:</p>
                      <button
                        type="button"
                        onClick={() => setStep('register')}
                        className="tap-target flex items-center justify-center gap-2 rounded-full bg-blue-50 px-5 py-2.5 text-sm font-bold text-blue-600 transition-all hover:text-blue-700"
                      >
                        <UserPlusIcon className="h-5 w-5" />
                        Register New Patient
                      </button>
                    </div>
                  </div>

                  {/* SECTION B: REGISTRATION (Matches Picture 4) */}
                  {(step === 'register' || selectedPatient) && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm duration-300 sm:rounded-[24px] sm:p-6 md:p-8">
                      <label className="text-xs font-black text-blue-600 uppercase tracking-widest mb-6 block">
                        Section B - Patient Registration Form
                      </label>
                      {selectedPatient ? (
                        <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 px-5 py-4">
                          <p className="text-sm font-black text-blue-900">
                            Existing patient selected: {selectedPatient.name}
                            {selectedPatientCode ? ` | ID: ${selectedPatientCode}` : ''}
                          </p>
                        </div>
                      ) : null}
                      
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Patient Name *</label>
                          <div className="relative">
                            <UserIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              disabled={Boolean(selectedPatient)}
                              className={`w-full rounded-xl pl-12 pr-4 py-3.5 font-bold outline-none focus:ring-2 focus:ring-blue-600 ${selectedPatientFieldClasses}`}
                              placeholder="Full Name"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Patient WhatsApp *</label>
                          <div className="relative">
                            <PhoneIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              disabled={Boolean(selectedPatient)}
                              className={`w-full rounded-xl pl-12 pr-4 py-3.5 font-bold outline-none focus:ring-2 focus:ring-blue-600 ${selectedPatientFieldClasses}`}
                              placeholder="10-digit number"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Patient Age</label>
                          <input
                            type="number"
                            value={formData.age}
                            onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                            disabled={Boolean(selectedPatient)}
                            className={`w-full rounded-xl px-4 py-3.5 font-bold outline-none focus:ring-2 focus:ring-blue-600 ${selectedPatientFieldClasses}`}
                            placeholder="Age in years"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Patient Gender</label>
                          <select
                            value={formData.gender}
                            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            disabled={Boolean(selectedPatient)}
                            className={`w-full rounded-xl px-4 py-3.5 font-bold outline-none appearance-none focus:ring-2 focus:ring-blue-600 ${selectedPatientFieldClasses}`}
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Patient Address</label>
                          <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            disabled={Boolean(selectedPatient)}
                            className={`w-full rounded-xl px-4 py-3.5 font-bold outline-none focus:ring-2 focus:ring-blue-600 ${selectedPatientFieldClasses}`}
                            placeholder="Full residential address"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Profession</label>
                          <input
                            type="text"
                            value={formData.profession}
                            onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                            disabled={Boolean(selectedPatient)}
                            className={`w-full rounded-xl px-4 py-3.5 font-bold outline-none focus:ring-2 focus:ring-blue-600 ${selectedPatientFieldClasses}`}
                            placeholder="Profession"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Visit Type *</label>
                          <select
                            value={formData.visitType}
                            onChange={(e) => setFormData({ ...formData, visitType: e.target.value })}
                            className="w-full bg-blue-50 border border-blue-200 text-blue-900 rounded-xl px-4 py-4 focus:ring-2 focus:ring-blue-600 outline-none font-black text-sm appearance-none"
                          >
                            <option value="New Consultation">New Consultation</option>
                            <option value="Follow-up">Follow-up</option>
                          </select>
                        </div>

                        {formData.visitType === 'Follow-up' && (
                          <div className="md:col-span-2">
                            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Previous Case Reference</label>
                            <input
                              type="text"
                              value={formData.previousCaseReference}
                              onChange={(e) => setFormData({ ...formData, previousCaseReference: e.target.value })}
                              className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-4 py-3.5 focus:ring-2 focus:ring-blue-600 outline-none font-bold"
                              placeholder="Existing case, prescription, or treatment note reference"
                            />
                          </div>
                        )}
                      </div>

                      <div className="mt-8 bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h4 className="text-base font-black text-slate-900">Appointment Scheduling</h4>
                            <p className="mt-1 text-sm font-medium text-slate-500">Choose whether to open the Prescription Pad immediately or save the appointment for later.</p>
                          </div>
                          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                            <button
                              type="button"
                              onClick={() => openSchedulingModal('now')}
                              disabled={submitting}
                              className="tap-target rounded-full bg-blue-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 disabled:opacity-50"
                            >
                              Schedule Now
                            </button>
                            <button
                              type="button"
                              onClick={() => openSchedulingModal('later')}
                              disabled={submitting}
                              className="tap-target rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-50"
                            >
                              Schedule Later
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* FOOTER ACTIONS */}
                <div className="mobile-sticky-actions flex flex-col items-stretch justify-between gap-3 rounded-b-[1.5rem] border-t border-slate-100 bg-white px-4 py-4 sm:flex-row sm:items-center sm:rounded-b-[2rem] sm:px-8 sm:py-6">
                  <button
                    type="button"
                    onClick={() => (step === 'register' ? setStep('search') : handleClose())}
                    className="tap-target rounded-full px-6 py-3 text-sm font-bold text-slate-600 transition-all hover:bg-slate-100"
                  >
                    {step === 'register' ? 'Back to Search' : 'Cancel'}
                  </button>

                  {step === 'register' ? (
                    <p className="text-sm font-semibold text-slate-500">Choose Schedule Now or Schedule Later above.</p>
                  ) : null}
                </div>
                </>}

              </Dialog.Panel>
            </Transition.Child>
            {schedulingDialog}
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
